/** Общие кусочки UI «Аналитики МП»: ошибка API, загрузка, сегмент-кнопки, компактная ячейка. */
import { PanelRight, RefreshCw } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";
import { TONE_CLASS } from "./calc";
import type { CellText } from "./compact";
import { useMp } from "./mp";
import { apiErrorText } from "./ui";

export function ErrorBox({ error, what }: { error: unknown; what: string }): React.ReactElement {
  const mp = useMp();
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
    >
      Не удалось получить {what}. {apiErrorText(error, mp.label)}
    </div>
  );
}

export function Loading({ text }: { text: string }): React.ReactElement {
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground"
    >
      <RefreshCw className="size-4 animate-spin" aria-hidden />
      {text}
    </div>
  );
}

/** Сегмент-кнопки (одна выбрана). */
export function Seg<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T | null;
  options: { id: T; label: string; noData?: boolean }[];
  onChange: (v: T) => void;
  label: string;
}): React.ReactElement {
  return (
    <div role="group" aria-label={label} className="inline-flex h-8 items-center rounded-md border bg-background p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            "inline-flex h-6 items-center rounded px-2.5 text-xs font-medium transition-colors",
            value === o.id
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            o.noData && "italic opacity-60",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Компактная ячейка — значение крупно, под ним мелко доля / Δ.
 *  Высота строк ≤ 36 px: leading-4 + leading-3 + py-1. */
export function CompactTd({
  cell,
  className,
}: {
  cell: CellText;
  className?: string;
}): React.ReactElement {
  const mainClass =
    cell.mainTone === "negative"
      ? "text-red-600 dark:text-red-400"
      : cell.mainTone
        ? TONE_CLASS[cell.mainTone]
        : "";
  return (
    <td
      className={cn("px-2 py-1 text-right tabular-nums whitespace-nowrap", className)}
      title={cell.title ?? undefined}
    >
      <div className={cn("leading-4", mainClass)}>{cell.main}</div>
      {cell.sub.length > 0 && (
        <div className="text-[11px] font-normal leading-3 text-muted-foreground">
          {cell.sub.map((p, i) => (
            <React.Fragment key={i}>
              {i > 0 && " · "}
              <span className={p.tone ? TONE_CLASS[p.tone] : undefined}>{p.text}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </td>
  );
}

/** Значок «открыть карточку сущности» (боковая панель) у узла дерева / строки таблицы: клик не
 *  раскрывает строку. `label` — доступное имя («Карточка поставки 123»). */
export function CardButton({
  label,
  title,
  onOpen,
}: {
  label: string;
  title: string;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      className="mt-0.5 inline-flex shrink-0 text-muted-foreground hover:text-foreground"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      <PanelRight className="size-3.5" aria-hidden />
    </button>
  );
}

/** Значок «Δ ненадёжна»: период сравнения вышел за данные витрины. */
export function Unreliable({
  warning,
}: {
  warning: string | null;
}): React.ReactElement | null {
  if (!warning) return null;
  return (
    <span
      className="ml-1 cursor-help text-amber-600 dark:text-amber-400"
      title={`Δ ненадёжна: ${warning}`}
      data-testid="delta-unreliable"
    >
      ⚠
    </span>
  );
}
